/**
 * 参照列 (過去実績／確定履歴) の「採用」ボタンの undo state machine を所有する
 * headless hook (spec AC-DFD-047/047a/047b, AC-DFD-048/048a、design note
 * tmp/designing-feature/demand-adopt-reference-column.md)。
 *
 * useForecastDraftEditor (god-hook) から「採用」の reducer + ref mirror クラスタを
 * 切り出した seam。責務は採用中 undo state (小売単位、Decision 3) の所有と、
 * 採用／解除 (sticky トグル) の editRows への反映のみ。page を render せず
 * renderHook で単体テストできる (editRows / setEditRows を props で受ける)。
 *
 * save flow との結合 (保存成功で採用中 undo をクリアする Decision 3) は、
 * 「保存開始時点の採用エントリ」を識別する必要があるため、タイミングを持つ
 * useDraftSaveQueue から `captureAdoptedEntry` / `clearAdoptedEntryIfUnchanged`
 * (identity-guarded クリア) を callback で呼ぶ形で分離する (state の意味論は本 hook、
 * 「いつ捕捉・いつクリアするか」の save タイミングは save queue が持つ)。
 *
 * BG 全体×配下複数の per-retailer 採用 (AC-DFD-047d / 048):
 * 既存の小売単位 keyed undo state をそのまま複数小売へ同時適用する形で拡張する
 * (`handleAdoptReferenceColumnForRetailers`)。per-retailer データ (過去実績の
 * 帰属値 / 確定履歴の bgResults) の解決・現在 BG 配下との絞り込み・値の無い
 * 内訳の除外は呼び出し元 (index.tsx) が行い、本 hook は解決済みの
 * `{retailerBusinessCd, slots}[]` を受け取るだけ (Decision 5)。
 *
 * 内訳「帰属不明」(AC-DFD-047c / 047d、issue #11461) も集計採用の対象で、
 * `UNATTRIBUTED_SAVE_STATE_KEY` を key にした entry として小売と同じ undo state に
 * 載せる (採用中・解除・保存成功・破棄の規則を小売と共有する)。帰属不明は小売
 * 表示に相当する単独採用を持たない (帰属不明の表示単位からの採用は禁止)。
 *
 * 「採用中」は採用した表示単位でだけ表示・解除する (AC-DFD-047b、issue #7436)。
 * 各小売の entry に採用した表示単位 (`origin`) を持たせ、集計表示で採用した
 * entry は小売表示の「採用中」に出さない (逆も同じ)。集計表示の「採用中」は、
 * 直近の集計採用で書き込んだ小売全員が、その採用の entry (`adoptionId` 一致)
 * を保持している時だけ成立する (`resolveAggregateAdoptedKey`)。
 */
import { useCallback, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { adoptReferenceColumnSlots } from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  RetailerEditRow,
  UnattributedEditRow,
} from '~/pages/DemandForecastDaily/models/types'

import { UNATTRIBUTED_SAVE_STATE_KEY } from './useDraftSaveQueue'

/**
 * 参照列「採用」undo state (Decision 1/2/3): 採用直前の編集可能な 3 field
 * (forecastSlots/manualEditKomas/providerLabel) の snapshot。未保存編集
 * セッション内の一時 state であり、小売単位で保持する (Decision 3)。
 */
export type AdoptedReferenceColumnState = {
  key: string
  /**
   * 採用した表示単位 (AC-DFD-047b)。'retailer' = 小売電気事業者表示 (配下 1 件の
   * 集計表示を含む) の単独採用、'aggregate' = 集計表示 (配下複数) の一括採用。
   */
  origin: 'retailer' | 'aggregate'
  /** 集計採用 1 回ごとの識別子 (同一回の集計採用で書いた entry 群を束ねる)。 */
  adoptionId: number
  snapshot: {
    forecastSlots: KomaSlots
    manualEditKomas: Set<number>
    providerLabel: string | undefined
  }
}

/**
 * 保存開始時点に捕捉した採用エントリの識別トークン。save queue が保存成功時の
 * identity-guarded クリア (`clearAdoptedEntryIfUnchanged`) にそのまま渡し戻す
 * 不透明値で、参照同一性 (===) の比較にのみ用いる (中身は解釈しない)。
 */
export type AdoptedReferenceColumnToken =
  | AdoptedReferenceColumnState
  | undefined

/** 直近の集計採用 (AC-DFD-047b / 047d / 048) の記録。 */
type AggregateAdoption = {
  key: string
  adoptionId: number
  /**
   * その採用で実際に値を書き込んだ内訳 (値の無い内訳は含まない、AC-DFD-047e)。
   * 帰属不明へ書き込んだ時は `UNATTRIBUTED_SAVE_STATE_KEY` を含む。
   */
  retailerBusinessCds: string[]
}

/**
 * 集計表示の「採用中」列 key を解決する純関数 (AC-DFD-047b)。直近の集計採用で
 * 書き込んだ小売全員が、その採用の entry をまだ保持している時だけ key を返す。
 * 1 人でも保存・破棄・衝突解消・小売表示での別採用で entry が置き換わって
 * いれば undefined (安全側、Decision 4)。
 */
function resolveAggregateKey(
  adoptedColumns: Record<string, AdoptedReferenceColumnState | undefined>,
  aggregate: AggregateAdoption | undefined,
): string | undefined {
  if (aggregate === undefined) return undefined
  if (aggregate.retailerBusinessCds.length === 0) return undefined
  return aggregate.retailerBusinessCds.every((cd) => {
    const entry = adoptedColumns[cd]
    return (
      entry !== undefined &&
      entry.origin === 'aggregate' &&
      entry.adoptionId === aggregate.adoptionId
    )
  })
    ? aggregate.key
    : undefined
}

/** 採用対象コマ (GC ロック外かつ参照列が値を持つコマ) が 1 つでもあるか。 */
function hasAdoptableKoma(slots: KomaSlots, gcKoma: number): boolean {
  for (let i = 0; i < 48; i++) {
    if (gcKoma > 0 && i + 1 <= gcKoma) continue
    if (slots[i] !== undefined) return true
  }
  return false
}

/** 採用・解除が書き換える editRow の field (小売と帰属不明で共通)。 */
type AdoptableFields = Pick<
  RetailerEditRow,
  'forecastSlots' | 'manualEditKomas' | 'providerLabel'
>

type UseAdoptReferenceColumnParams = {
  editRows: Record<string, RetailerEditRow>
  setEditRows: Dispatch<SetStateAction<Record<string, RetailerEditRow>>>
  /** 内訳「帰属不明」の未保存 buffer (BG 全体の集計採用だけが書き込む)。 */
  unattributedRow?: UnattributedEditRow
  setUnattributedRow?: Dispatch<SetStateAction<UnattributedEditRow | undefined>>
  selectedRetailerBusinessCd: string | undefined
  gcKoma: number
  canMutateNow: () => boolean
}

/**
 * `handleAdoptReferenceColumn` の結果 (需要想定 過去実績「採用」の小売別取込
 * Design Note レビュー指摘): 呼び出し元 (index.tsx) が「実際に新規採用したか」
 * (= 帰属不明分通知の発火条件、Decision 7) を知るために、内部 gate
 * (`canMutateNow` / `selectedRetailerBusinessCd===undefined` / `gcKoma>=48` /
 * `row===undefined`) を再導出せずこの値だけを見れば良いようにする。
 *
 * - 'adopted': 新規採用または別列からの切替が実際に反映された。
 * - 'unadopted': 採用中の同列再クリックで解除された (Decision 1 のトグル)。
 * - 'noop': いずれかの内部 gate で拒否され、何も変わっていない。
 */
export type AdoptReferenceColumnOutcome = 'adopted' | 'unadopted' | 'noop'

export type UseAdoptReferenceColumnResult = {
  /**
   * 選択中小売の「採用中」参照列キー (AC-DFD-047b: 同時に採用中は最大 1 列)。
   * undefined = 採用中の列なし。
   */
  adoptedReferenceColumnKey: string | undefined
  /**
   * 参照列「採用」ボタン押下 (トグル/採用切替)。戻り値
   * (`AdoptReferenceColumnOutcome`) で呼び出し元に結果を伝える。
   */
  handleAdoptReferenceColumn: (column: {
    key: string
    sourceLabel: string
    slots: KomaSlots
  }) => AdoptReferenceColumnOutcome
  /**
   * BG 全体×配下複数の per-retailer 採用 (AC-DFD-047d / 048, Decision 3/4/5/7)。
   * 集計表示で同じ列が採用中 (`resolveAggregateAdoptedKey`) なら、その採用で
   * 書き込んだ小売全員を採用直前の値へ一括 undo する (AC-DFD-047b)。それ以外は
   * `retailerSlots` (呼び出し元が解決済みの per-retailer 値、Decision 5) の
   * うち採用対象コマを持つ小売の editRow へ一括反映し、それぞれの採用直前状態を
   * 退避する (単一採用の Decision 1/2/4 を複数小売へ同時適用したもの)。
   * `sourceLabel` は各小売の providerLabel にそのまま設定する (Decision 7)。
   * `unattributedSlots` を渡すと内訳「帰属不明」にも同じ規則で反映する
   * (AC-DFD-047c / 047d。帰属不明の buffer が無い時は無視する)。
   */
  handleAdoptReferenceColumnForRetailers: (column: {
    key: string
    sourceLabel: string
    retailerSlots: Array<{ retailerBusinessCd: string; slots: KomaSlots }>
    unattributedSlots?: KomaSlots
  }) => AdoptReferenceColumnOutcome
  /**
   * 集計表示の「採用中」列 key (AC-DFD-047b)。呼び出し元 (index.tsx) が BG
   * 集計ヘッダーの「採用中」表示に使う。
   */
  resolveAggregateAdoptedKey: () => string | undefined
  /**
   * 保存開始時点の採用エントリ (オブジェクト参照) を捕捉する。採用のたびに新しい
   * オブジェクトを生成するため、参照一致は「同一インスタンスの採用」を厳密に表す
   * (別列採用・同一列の解除→再採用は別インスタンス)。save queue が保存成功時の
   * クリア判定に使う。
   */
  captureAdoptedEntry: (
    retailerBusinessCd: string,
  ) => AdoptedReferenceColumnToken
  /**
   * Decision 3 (保存で確定): 保存開始時点と同一インスタンスの採用に限って undo
   * state をクリアする。保存 RPC 応答前に別列を採用した場合・同じ列を解除→再採用
   * した場合 (別インスタンス) は、その新採用の undo を消さない。
   */
  clearAdoptedEntryIfUnchanged: (
    retailerBusinessCd: string,
    entryAtSaveStart: AdoptedReferenceColumnToken,
  ) => void
  /**
   * Decision 3 (破棄／衝突解消／取込): 対象小売の採用中 undo state を無条件に
   * 解除する (baseline 復元・draft 全差し替えで採用中を残すと再押下で採用直前へ
   * 巻き戻り未保存値が失われるため)。
   */
  clearAdopted: (retailerBusinessCds: string[]) => void
}

export function useAdoptReferenceColumn({
  editRows,
  setEditRows,
  unattributedRow,
  setUnattributedRow,
  selectedRetailerBusinessCd,
  gcKoma,
  canMutateNow,
}: UseAdoptReferenceColumnParams): UseAdoptReferenceColumnResult {
  // 採用中 undo state (小売単位、Decision 3)。保存/破棄/衝突解消/取込で対象小売の
  // entry をクリアする。
  const [adoptedReferenceColumns, setAdoptedReferenceColumns] = useState<
    Record<string, AdoptedReferenceColumnState | undefined>
  >({})

  // 採用中 undo state の同期スナップショット (editRowsRef と同じ理由)。save queue の
  // captureAdoptedEntry が「保存開始時点」の採用エントリを読むのに使う (保存 RPC
  // 応答前に別列を採用した場合、保存成功のクリアで新採用の undo を消さないため)。
  const adoptedReferenceColumnsRef = useRef(adoptedReferenceColumns)
  adoptedReferenceColumnsRef.current = adoptedReferenceColumns

  // 直近の集計採用 (AC-DFD-047b)。entry 側が保存等で消えれば
  // resolveAggregateKey が自然に不成立になるため、ここは明示クリアしない。
  const [aggregateAdoption, setAggregateAdoption] = useState<
    AggregateAdoption | undefined
  >(undefined)
  // 集計採用 1 回ごとの識別子の採番元。
  const nextAdoptionIdRef = useRef(1)

  // ── handleAdoptReferenceColumn ─────────────────────────────────────
  //
  // Decision 2 (sticky モデル): 採用中は同時 1 列のみ。別列を採用すると前の
  // 採用中は解除され、undo snapshot は「新採用直前」(= 前の採用の反映結果 +
  // その後の手動編集を含む現在値) で更新する。採用後の手動編集は採用中の
  // まま維持され、解除は snapshot への一括復元になる (採用後の手動編集も破棄)。
  // Decision 1 (トグル): 採用中の同列を再度押下すると、採用直前の
  // forecastSlots/manualEditKomas/providerLabel へ復元し undo state を消す。
  // Decision 4: 参照列の空コマ・GC 済みコマは skip する (adoptReferenceColumnSlots)。
  // Decision 5: 採用元サブラベル (providerLabel) は呼び出し元 (KomaGrid) が
  // 算出した `column.sourceLabel` をそのまま設定する。
  const handleAdoptReferenceColumn = useCallback(
    (column: {
      key: string
      sourceLabel: string
      slots: KomaSlots
    }): AdoptReferenceColumnOutcome => {
      if (!canMutateNow()) return 'noop'
      if (selectedRetailerBusinessCd === undefined) return 'noop'
      // AC-DFD-047b: 当日断面で全コマ GC 済み (gcKoma>=48) の場合は値変更不可。
      // 反映は全コマ skip で no-op になるが、providerLabel/採用中 state だけが立って
      // 未保存化するのを防ぐため、採用・解除ともにここで拒否する (UI 側の非活性と
      // 同条件。セル編集・簡易予測取込と共通の値変更不可条件)。
      if (gcKoma >= 48) return 'noop'
      const retailerBusinessCd = selectedRetailerBusinessCd
      const row = editRows[retailerBusinessCd]
      if (row === undefined) return 'noop'
      const adopted = adoptedReferenceColumns[retailerBusinessCd]

      // AC-DFD-047b: 「採用中」の解除は採用した表示単位からだけ。集計表示で
      // 採用した entry は小売表示では採用中ではないため、同じ列でも新規採用に
      // なる (採用直前 = 集計採用の反映結果を含む現在値)。
      if (
        adopted !== undefined &&
        adopted.origin === 'retailer' &&
        adopted.key === column.key
      ) {
        // Decision 1: 採用中の同列再クリック → 採用直前 snapshot へ復元する。
        const { snapshot } = adopted
        setEditRows((prev) => {
          const current = prev[retailerBusinessCd]
          if (current === undefined) return prev
          return {
            ...prev,
            [retailerBusinessCd]: {
              ...current,
              forecastSlots: snapshot.forecastSlots,
              manualEditKomas: new Set(snapshot.manualEditKomas),
              providerLabel: snapshot.providerLabel,
            },
          }
        })
        setAdoptedReferenceColumns((prev) => ({
          ...prev,
          [retailerBusinessCd]: undefined,
        }))
        return 'unadopted'
      }

      // Decision 2/4: 採用 (新規、または別列からの切替)。
      const nextSlots = adoptReferenceColumnSlots(
        row.forecastSlots,
        column.slots,
        gcKoma,
      )
      const newManual = new Set(row.manualEditKomas)
      for (let i = 0; i < 48; i++) {
        if (nextSlots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
      }
      // 採用対象コマ = GC ロック外 かつ 参照列が値を持つコマ。1 つも無ければ
      // adoptReferenceColumnSlots は現在値をそのまま返し反映が起こりえないので、
      // 内部 gate と同じ noop として扱う (当日断面で参照列の定義済み値が全て
      // GC 済みコマに収まるケース)。
      //
      // 判定に「値が現在値と変わるか」を使わないのは、AC-DFD-047a の採用条件が
      // 「クリックした時」であって差分の有無ではないため。保存で採用中 token だけ
      // クリアされた後に同じ列を再クリックすると値は既に一致しており、差分で
      // 判定すると採用中表示 (AC-DFD-047b) と取込元ラベルが更新されず無反応に
      // 見える。「採用対象コマの有無」と「値が変わったか」は別の関心として分ける。
      if (!hasAdoptableKoma(column.slots, gcKoma)) return 'noop'

      // Decision 2: 現在値 (前の採用中があればその反映結果 + 手動編集込み) を
      // 「新採用直前」の snapshot として退避する。
      setAdoptedReferenceColumns((prev) => ({
        ...prev,
        [retailerBusinessCd]: {
          key: column.key,
          origin: 'retailer',
          adoptionId: 0,
          snapshot: {
            forecastSlots: row.forecastSlots,
            manualEditKomas: new Set(row.manualEditKomas),
            providerLabel: row.providerLabel,
          },
        },
      }))
      setEditRows((prev) => {
        const current = prev[retailerBusinessCd]
        if (current === undefined) return prev
        return {
          ...prev,
          [retailerBusinessCd]: {
            ...current,
            forecastSlots: nextSlots,
            manualEditKomas: newManual,
            providerLabel: column.sourceLabel,
          },
        }
      })
      return 'adopted'
    },
    [
      editRows,
      setEditRows,
      adoptedReferenceColumns,
      selectedRetailerBusinessCd,
      gcKoma,
      canMutateNow,
    ],
  )

  // 集計採用が書き換える内訳の行 (小売は editRows、帰属不明は unattributedRow)。
  const aggregateRowOf = useCallback(
    (key: string): AdoptableFields | undefined =>
      key === UNATTRIBUTED_SAVE_STATE_KEY ? unattributedRow : editRows[key],
    [editRows, unattributedRow],
  )

  // keys の各内訳の行へ patch を当てる。patch は現在の行から新しい 3 field を返す
  // (undefined なら変えない)。
  const patchAggregateRows = useCallback(
    (
      keys: string[],
      patch: (key: string, row: AdoptableFields) => AdoptableFields | undefined,
    ): void => {
      const retailerKeys = keys.filter(
        (key) => key !== UNATTRIBUTED_SAVE_STATE_KEY,
      )
      if (retailerKeys.length > 0) {
        setEditRows((prev) => {
          const next = { ...prev }
          for (const key of retailerKeys) {
            const current = prev[key]
            if (current === undefined) continue
            const fields = patch(key, current)
            if (fields !== undefined) next[key] = { ...current, ...fields }
          }
          return next
        })
      }
      if (keys.includes(UNATTRIBUTED_SAVE_STATE_KEY)) {
        setUnattributedRow?.((current) => {
          if (current === undefined) return current
          const fields = patch(UNATTRIBUTED_SAVE_STATE_KEY, current)
          return fields === undefined ? current : { ...current, ...fields }
        })
      }
    },
    [setEditRows, setUnattributedRow],
  )

  // ── handleAdoptReferenceColumnForRetailers (BG 全体×配下複数の per-retailer 採用) ──
  //
  // Decision 3: 上の handleAdoptReferenceColumn (単一小売) と同じ undo/snapshot
  // 意味論を、呼び出し元が解決済みの複数小売 (retailerSlots) と帰属不明
  // (unattributedSlots) へ同時適用する。
  // AC-DFD-047b (トグル、集計表示版): 集計表示で同じ列が採用中なら、その採用で
  // 書き込んだ内訳全員を一括 undo する (解除範囲 = 採用した範囲)。それ以外は
  // 採用対象コマを持つ内訳全員へ「新採用直前」の snapshot を退避しつつ反映する
  // (対象内訳それぞれの「現在値」を退避するので個々の手動編集差異があっても
  // 矛盾しない)。
  const handleAdoptReferenceColumnForRetailers = useCallback(
    (column: {
      key: string
      sourceLabel: string
      retailerSlots: Array<{ retailerBusinessCd: string; slots: KomaSlots }>
      unattributedSlots?: KomaSlots
    }): AdoptReferenceColumnOutcome => {
      if (!canMutateNow()) return 'noop'
      // AC-DFD-047b/048a: 当日断面で全コマ GC 済みは値変更不可 (単一採用と同条件)。
      if (gcKoma >= 48) return 'noop'

      if (
        aggregateAdoption !== undefined &&
        resolveAggregateKey(adoptedReferenceColumns, aggregateAdoption) ===
          column.key
      ) {
        // AC-DFD-047b: 採用中の同列再クリック → 採用した内訳全員を採用直前
        // snapshot へ一括復元する (手修正も含めて戻す)。
        const targetKeys = aggregateAdoption.retailerBusinessCds
        patchAggregateRows(targetKeys, (key) => {
          const adopted = adoptedReferenceColumns[key]
          if (adopted === undefined) return undefined
          return {
            forecastSlots: adopted.snapshot.forecastSlots,
            manualEditKomas: new Set(adopted.snapshot.manualEditKomas),
            providerLabel: adopted.snapshot.providerLabel,
          }
        })
        setAdoptedReferenceColumns((prev) => {
          const next = { ...prev }
          for (const key of targetKeys) next[key] = undefined
          return next
        })
        setAggregateAdoption(undefined)
        return 'unadopted'
      }

      // 採用対象: 行があり、GC ロック外に値を持つ内訳だけ (単一採用の
      // hasAdoptableKoma と同じ判定。1 件も無ければ採用中も立てない)。
      const entries = [
        ...column.retailerSlots.map((rs) => ({
          key: rs.retailerBusinessCd,
          slots: rs.slots,
        })),
        ...(column.unattributedSlots === undefined
          ? []
          : [
              {
                key: UNATTRIBUTED_SAVE_STATE_KEY,
                slots: column.unattributedSlots,
              },
            ]),
      ]
      const targets = entries.filter(
        (entry) =>
          aggregateRowOf(entry.key) !== undefined &&
          hasAdoptableKoma(entry.slots, gcKoma),
      )
      if (targets.length === 0) return 'noop'

      const adoptionId = nextAdoptionIdRef.current++
      // Decision 2/4/7: 採用 (新規、または別列からの切替) を対象内訳全員へ一括適用。
      // snapshot は各内訳の「反映前の現在値」(既存単一採用と同じ退避規律)。
      setAdoptedReferenceColumns((prev) => {
        const next = { ...prev }
        for (const entry of targets) {
          const row = aggregateRowOf(entry.key)
          if (row === undefined) continue
          next[entry.key] = {
            key: column.key,
            origin: 'aggregate',
            adoptionId,
            snapshot: {
              forecastSlots: row.forecastSlots,
              manualEditKomas: new Set(row.manualEditKomas),
              providerLabel: row.providerLabel,
            },
          }
        }
        return next
      })
      const slotsByKey = new Map(
        targets.map((entry) => [entry.key, entry.slots]),
      )
      patchAggregateRows(
        targets.map((entry) => entry.key),
        (key, row) => {
          const slots = slotsByKey.get(key)
          if (slots === undefined) return undefined
          const nextSlots = adoptReferenceColumnSlots(
            row.forecastSlots,
            slots,
            gcKoma,
          )
          const newManual = new Set(row.manualEditKomas)
          for (let i = 0; i < 48; i++) {
            if (nextSlots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
          }
          return {
            forecastSlots: nextSlots,
            manualEditKomas: newManual,
            // Decision 7: 各内訳の editRow にも採用元サブラベルを設定する。
            providerLabel: column.sourceLabel,
          }
        },
      )
      setAggregateAdoption({
        key: column.key,
        adoptionId,
        retailerBusinessCds: targets.map((entry) => entry.key),
      })
      return 'adopted'
    },
    [
      aggregateRowOf,
      patchAggregateRows,
      adoptedReferenceColumns,
      aggregateAdoption,
      gcKoma,
      canMutateNow,
    ],
  )

  const resolveAggregateAdoptedKey = useCallback(
    (): string | undefined =>
      resolveAggregateKey(adoptedReferenceColumns, aggregateAdoption),
    [adoptedReferenceColumns, aggregateAdoption],
  )

  const captureAdoptedEntry = useCallback(
    (retailerBusinessCd: string): AdoptedReferenceColumnToken =>
      adoptedReferenceColumnsRef.current[retailerBusinessCd],
    [],
  )

  const clearAdoptedEntryIfUnchanged = useCallback(
    (
      retailerBusinessCd: string,
      entryAtSaveStart: AdoptedReferenceColumnToken,
    ): void => {
      setAdoptedReferenceColumns((prev) =>
        prev[retailerBusinessCd] === entryAtSaveStart
          ? { ...prev, [retailerBusinessCd]: undefined }
          : prev,
      )
    },
    [],
  )

  const clearAdopted = useCallback((retailerBusinessCds: string[]): void => {
    if (retailerBusinessCds.length === 0) return
    setAdoptedReferenceColumns((prev) => {
      let changed = false
      const next = { ...prev }
      for (const retailerBusinessCd of retailerBusinessCds) {
        if (next[retailerBusinessCd] !== undefined) {
          next[retailerBusinessCd] = undefined
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  // AC-DFD-047b: 選択中小売の「採用中」参照列キー (KomaGrid の is-adopted 判定用)。
  // 集計表示で採用した entry は小売表示の「採用中」に出さない (採用した表示単位
  // でだけ表示する)。
  const selectedEntry =
    selectedRetailerBusinessCd !== undefined
      ? adoptedReferenceColumns[selectedRetailerBusinessCd]
      : undefined
  const adoptedReferenceColumnKey: string | undefined =
    selectedEntry?.origin === 'retailer' ? selectedEntry.key : undefined

  return {
    adoptedReferenceColumnKey,
    handleAdoptReferenceColumn,
    handleAdoptReferenceColumnForRetailers,
    resolveAggregateAdoptedKey,
    captureAdoptedEntry,
    clearAdoptedEntryIfUnchanged,
    clearAdopted,
  }
}
